import { useAuth0 } from "@auth0/auth0-react";
import Typography from "@mui/material/Typography";
import AlertDialog from "components/Dialog/index";
import { InputField } from "components/InputField";
import { useFormik } from "formik";
import PastoralService from "Lib/API/PastoralApi/index";
import React, { useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import AddGroupMembers from "./AddGroupMembers";
import "./index.scss";

function AddGroup({ type, closeDrawer,handleClose, fetchGroups, groupData }) {
	const { getAccessTokenSilently } = useAuth0();
	const [isLoading, setIsLoading] = useState(false);
	const [getContactsLoading, setGetContactsLoading] = useState(false);
	const [contactsList, setContactsList] = useState([]);
	const [selectedContact, setSelectedContact] = useState({});
	const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedContactAlert, setSelectedContactAlert] = useState(false);
	const [confirmModal, setConfirmModal] = useState({
		open: false,
		message: "",
		description: "",
		loading: false,
		type: "",
	});

	const initialValues = {
		name: groupData?.label || "",
		description: groupData?.memo || "",
		addNewContact: false,
		selectedContact: {},
		contacts: [],
	};

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: initialValues,

		onSubmit: async (values) => {
			if (!checkValidation()) return;
			const subscribers = values?.contacts?.length
				? values?.contacts.map((contact) => {
						return {
							contactID: contact.value,
							defaultReciveAlerts: contact.alert,
						};
				  })
				: [];
			const input = {
				name: values?.name,
				memo: values?.description,
				subscribers: subscribers?.length && type !== "edit" ? subscribers : undefined,
			};
			if (type === "edit") {
				editGroupHandler(input);
			} else {
				createGroupHandler(input);
			}
		},
	});

	const getContactsHandler = async () => {
		setGetContactsLoading(true);
		const token = await getAccessTokenSilently();
		PastoralService.getContacts({ token, page: 1 })
			.then((res) => {
				if (res) {
					const formattedContacts = res?.contacts?.map((item) => ({
						label: item?.fullName,
						value: item?.id,
						alert: false,
					}));
					setContactsList(formattedContacts);
				}
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				setGetContactsLoading(false);
			});
	};

	const getContactsByGroupId = async () => {
		const groupId = groupData?.value;
		if (!groupId) return;
		const token = await getAccessTokenSilently();
		PastoralService.getContactsByGroupId({ token, groupId })
			.then((res) => {
				if (res) {
					const selectedContacts = res?.data?.map((item) => ({
						label: `${item?.fname} ${item?.lname}`,
						value: item?.id,
						alert: item?.defaultReciveAlerts,
					}));
					formik.setFieldValue("contacts", selectedContacts);
					const contactListFiltered = contactsList.filter(
						(contact) => !selectedContacts.some((selected) => selected.value === contact.value)
					);
					setContactsList(contactListFiltered);
				}
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {});
	};

	const createGroupHandler = async (payload) => {
		setIsLoading(true);
		const token = await getAccessTokenSilently();
		PastoralService.createGroup({ data: payload, token })
			.then((response) => {
				toastr.success("Success", "Group created successfully");
				handleClose();
				fetchGroups();
			})
			.catch((error) => {
				toastr.error("Error", error?.data?.message || "Something went wrong");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const editGroupHandler = async (payload) => {
		setIsLoading(true);
		const token = await getAccessTokenSilently();
		PastoralService.updateGroup({ data: payload, token, groupId: groupData?.value })
			.then((response) => {
				toastr.success("Success", "Group updated successfully");
				handleClose();
				fetchGroups();
			})
			.catch((error) => {
				toastr.error("Error", error?.data?.message || "Something went wrong");
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	useEffect(() => {
		getContactsHandler();
		if (type === "edit") {
			getContactsByGroupId();
		}
	}, []);

	const checkValidation = () => {
		if (!formik.values?.name) {
      toastr.info("validation", "Please enter group name");
			return false;
		}
		return true;
	};

	const addContents = (selected) => {
		const prevSelected = formik.values?.contacts || [];
		formik.setFieldValue("contacts", [...prevSelected, selected]);
		formik.setFieldValue("selectedContact", {});
		const contactListFiltered = contactsList.filter((contact) => contact.value !== selected.value);
		setContactsList(contactListFiltered);
	};

	const deleteContact = (id) => {
		const prevSelected = formik.values?.contacts || [];
		const contactListFiltered = prevSelected.filter((contact) => contact.value !== id);
		formik.setFieldValue("contacts", contactListFiltered);
		const contactList = [...contactsList];
		const contact = prevSelected.find((contact) => contact.value === id);
		contactList.push(contact);
		setContactsList(contactList);
	};

	const contactSelectedHandler = (selected) => {
		if (!selected) return;
		if (type === "edit") {
			setConfirmModal({
				open: true,
				message: "Are you sure?",
				description: "Do you want to add this contact to the group?",
				type: "add",
			});
			setSelectedContact(selected);
			return;
		}

		addContents(selected);
	};

	const deleteSelectedContact = (id) => {
		if (type === "edit") {
			setConfirmModal({
				open: true,
				message: "Are you sure?",
				description: "Do you want to delete this contact from the group?",
				type: "delete",
			});
			setSelectedContactId(id);
			return;
		}
		deleteContact(id);
	};

  const updateSelectedContact = (id, checked) => {
    const prevSelected = formik.values?.contacts || [];
		const contactListFiltered = prevSelected.map((contact) => {
			if (contact.value === id) {
				return {
					...contact,
					alert: checked,
				};
			}
			return contact;
		});
		formik.setFieldValue("contacts", contactListFiltered);
  }

	const alertSelectedContact = (id, checked) => {
    if(type === "edit") {
      setConfirmModal({
				open: true,
				message: "Are you sure?",
				description: "Do you want to update this contact alert status?",
				type: "update",
			});
      setSelectedContactId(id);
      setSelectedContactAlert(checked);
      return;
    }

		updateSelectedContact(id, checked);
	};

	const closeConfirmModal = () => {
		setConfirmModal({
			open: false,
			message: "",
			description: "",
			loading: false,
			type: "",
		});
	};

	const confirmAddContact = async () => {
		setConfirmModal({
			...confirmModal,
			loading: true,
		});
		const token = await getAccessTokenSilently();
		const payload = {
			contactID: selectedContact.value,
			defaultReciveAlerts: selectedContact.alert,
		};

		PastoralService.addContactToGroup({ token, groupId: groupData?.value, data: payload })
			.then((res) => {
				addContents(selectedContact);
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				closeConfirmModal();
			});
	};

	const confirmDeleteContact = async () => {
		setConfirmModal({
			...confirmModal,
			loading: true,
		});
		const token = await getAccessTokenSilently();
		const contactId = selectedContactId;
		const groupId = groupData?.value;

		PastoralService.deleteContactToGroup({ token, groupId, contactId })
			.then((res) => {
				deleteContact(contactId);
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				closeConfirmModal();
			});
	};

  const confirmUpdateContact = async () => {
		setConfirmModal({
			...confirmModal,
			loading: true,
		});
		const token = await getAccessTokenSilently();
		const contactId = selectedContactId;
		const groupId = groupData?.value;
    const payload = {
      defaultReciveAlerts: selectedContactAlert,
    };

		PastoralService.updateContactToGroup({ token, groupId, contactId,data: payload })
			.then((res) => {
				updateSelectedContact(contactId, selectedContactAlert);
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				closeConfirmModal();
			});
	};

	const onAcceptConfirm = () => {
		switch (confirmModal.type) {
			case "add":
				confirmAddContact();
				break;
			case "delete":
				confirmDeleteContact();
				break;
      case "update":
        confirmUpdateContact();
        break;
			default:
				break;
		}
	};

	return (
		<>
			<div className="add-group-section">
				<div className="add-group-header update__user__header ">
					<Typography variant="h5" gutterBottom>
						New Group
					</Typography>
					<div className="user__header__action">
						<button className="cancel_user_btn" onClick={closeDrawer}>
							Cancel
						</button>
						<button className="save_user_btn btn btn-primary" onClick={formik.handleSubmit} disabled={isLoading}>
							<span className="user_btn_text">Save</span>
							{isLoading && <span className="ml-3 spinner spinner-white"></span>}
						</button>
					</div>
				</div>

				<div className="group-input">
					<InputField
						{...{
							formik,
							title: "Name",
							name: "name",
							checkValidation: true,
							value: formik.values?.name,
						}}
						mandatory={true}
						titleStyle={{ fontSize: "12px" }}
					/>
					<InputField
						{...{
							formik,
							title: "Description",
							name: "description",
							checkValidation: true,
							value: formik.values?.description,
						}}
						titleStyle={{ fontSize: "12px" }}
					/>
				</div>
				<AddGroupMembers
					formik={formik}
					contactsList={contactsList}
					contactsLoading={getContactsLoading}
					contactSelectedHandler={contactSelectedHandler}
					deleteSelectedContact={deleteSelectedContact}
					alertSelectedContact={alertSelectedContact}
				/>
			</div>
			<AlertDialog
				open={confirmModal.open}
				onDecline={closeConfirmModal}
				onAccept={onAcceptConfirm}
				title={confirmModal.message}
				description={confirmModal.description}
				disabled={confirmModal.loading}
			/>
		</>
	);
}

export default AddGroup;
